import axiosInstance from '@/app/api/axiosInstance';
import { format, startOfMonth } from 'date-fns';
import { PaginatedResponse } from '@/app/types/api';
import { API_BASE_URL } from '@/app/config/api';

export interface DailySummary {
  summary_date: string;
  total_sales: number;
  total_payments_received: number;
  sales_invoice_count: number;
  payment_received_count: number;
}

export interface DashboardStats {
  activeCustomers: number;
  totalItems: number;
  lowStockAlerts: number;
  pendingOrders: number;
  pendingOrdersValue: number;
  monthToDateSales: number;
  monthToDateTarget: number;
}

export interface DashboardDateRangeStats {
  totalSales: number;
  salesCount: number;
  totalPayments: number;
  paymentsCount: number;
  startDate: string;
  endDate: string;
}

export interface RecentActivity {
  id: string;
  type: 'sale' | 'payment' | 'order' | 'expense';
  title: string;
  description: string;
  amount?: number;
  time: string;
  user?: {
    name: string;
    avatar?: string;
  };
}

export interface ComplianceDashboard {
  pending_count: number;
  overdue_count: number;
  due_this_week_count: number;
  filed_this_month_count: number;
  total_fee_pending?: number;
  total_fee_billed?: number;
  // Assignment-state breakdown (open tasks)
  unassigned_count?: number;
  individual_count?: number;
  team_pool_count?: number;
  team_assigned_count?: number;
  my_team_pool_count?: number;
}

export interface TeamActivity {
  id: number;
  user_id: number;
  user_name: string;
  action: 'completed' | 'created' | 'updated';
  task_id: number;
  task_title: string;
  timestamp: string;
}

export interface TaskCompletionStats {
  period: 'daily' | 'weekly';
  stats: Array<{
    user_id: number;
    user_name: string;
    completed_count: number;
    total_assigned: number;
    avatar_url?: string;
  }>;
  team_total_completed: number;
  team_target: number;
}

export const dashboardService = {
  async getDailySummary(date?: Date): Promise<DailySummary> {
    const params = date ? { summary_date: format(date, 'yyyy-MM-dd') } : {};
    const response = await axiosInstance.get<DailySummary>('/reports/daily-summary', { params });
    return response.data;
  },

  async getDashboardDateRangeStats(startDate: Date, endDate: Date): Promise<DashboardDateRangeStats> {
    try {
      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');
      
      // If it's a single day, use the daily summary endpoint which we know works
      if (start === end) {
        const dailySummary = await this.getDailySummary(startDate);
        return {
          totalSales: dailySummary.total_sales,
          salesCount: dailySummary.sales_invoice_count,
          totalPayments: dailySummary.total_payments_received,
          paymentsCount: dailySummary.payment_received_count,
          startDate: start,
          endDate: end
        };
      }
      
      // For date ranges, aggregate daily summaries
      const currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      const dailySummaries: DailySummary[] = [];
      
      while (currentDate <= endDateObj) {
        try {
          const summary = await this.getDailySummary(new Date(currentDate));
          dailySummaries.push(summary);
        } catch (error) {
          console.log(`No data for ${format(currentDate, 'yyyy-MM-dd')}`);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Aggregate the results
      const totalSales = dailySummaries.reduce((sum, day) => sum + day.total_sales, 0);
      const salesCount = dailySummaries.reduce((sum, day) => sum + day.sales_invoice_count, 0);
      const totalPayments = dailySummaries.reduce((sum, day) => sum + day.total_payments_received, 0);
      const paymentsCount = dailySummaries.reduce((sum, day) => sum + day.payment_received_count, 0);

      return {
        totalSales,
        salesCount,
        totalPayments,
        paymentsCount,
        startDate: start,
        endDate: end
      };
    } catch (error: any) {
      console.error('Error fetching dashboard date range stats:', error);
      return {
        totalSales: 0,
        salesCount: 0,
        totalPayments: 0,
        paymentsCount: 0,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      };
    }
  },

  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Get party count first
      let partyCount = 0;
      try {
        const partyResponse = await fetch(`${API_BASE_URL}/parties/?limit=1`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        const partyData = await partyResponse.json();
        partyCount = partyData.total || 0;
      } catch (e) {
        partyCount = 0;
      }

      // Fetch all data in parallel
      const [_, items, lowStock, pendingOrders, monthSales] = await Promise.all([
        Promise.resolve(null), // placeholder for parties since we already fetched it
        
        // Get total items
        axiosInstance.get<PaginatedResponse<any>>('/items/', {
          params: { limit: 1 }
        }),
        
        // Get low stock alerts
        axiosInstance.get<PaginatedResponse<any>>('/reports/low-stock-summary', {
          params: { limit: 1 }
        }),
        
        // Get pending sales orders
        axiosInstance.get<PaginatedResponse<any>>('/documents/', {
          params: { 
            limit: 100,
            filters: JSON.stringify([
              { field: 'document_type', value: 'Sales Order', op: 'eq' },
              { field: 'status', value: 'Pending', op: 'eq' }
            ])
          }
        }),
        
        // Get month to date sales
        this.getMonthToDateSales()
      ]);

      // Calculate pending orders value
      let pendingOrdersValue = 0;
      if (pendingOrders.data.items && pendingOrders.data.items.length > 0) {
        pendingOrdersValue = pendingOrders.data.items.length * 100000; // Rough estimate
      }

      const stats = {
        activeCustomers: partyCount,
        totalItems: items?.data?.total || 0,
        lowStockAlerts: lowStock?.data?.total || 0,
        pendingOrders: pendingOrders?.data?.total || 0,
        pendingOrdersValue,
        monthToDateSales: monthSales,
        monthToDateTarget: 1500000 // Default target, can be made configurable
      };

      console.warn('✅ Dashboard stats calculated:', stats);
      console.warn('🔢 Party count (activeCustomers):', stats.activeCustomers);
      return stats;
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }
      // Return default values on error
      return {
        activeCustomers: 0,
        totalItems: 0,
        lowStockAlerts: 0,
        pendingOrders: 0,
        pendingOrdersValue: 0,
        monthToDateSales: 0,
        monthToDateTarget: 1500000
      };
    }
  },

  async getMonthToDateSales(): Promise<number> {
    try {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const dateRangeStats = await this.getDashboardDateRangeStats(monthStart, now);
      return dateRangeStats.totalSales;
    } catch (error) {
      console.error('Error fetching month to date sales:', error);
      return 0;
    }
  },

  async getRecentActivities(): Promise<RecentActivity[]> {
    try {
      // Fetch all recent vouchers sorted by creation date
      const response = await axiosInstance.get<PaginatedResponse<any>>('/vouchers/', {
        params: {
          limit: 20,
          sort_by: 'created_at',
          sort_dir: 'desc'
        }
      });

      const activities: RecentActivity[] = [];

      // Process vouchers
      if (response.data.items) {
        response.data.items.forEach((voucher: any) => {
          // Map voucher types to activity types
          let activityType: 'sale' | 'payment' | 'order' | 'expense' = 'sale';
          let title = '';
          
          switch (voucher.voucher_type_id) {
            case 1: // Sales Invoice
              activityType = 'sale';
              title = 'New Sale Invoice';
              break;
            case 3: // Payment Made
            case 4: // Payment Received
              activityType = 'payment';
              title = voucher.voucher_type_id === 4 ? 'Payment Received' : 'Payment Made';
              break;
            case 2: // Purchase Invoice
              activityType = 'expense';
              title = 'Purchase Invoice';
              break;
            default:
              title = voucher.voucher_type?.name || 'Transaction';
          }

          activities.push({
            id: voucher.id.toString(),
            type: activityType,
            title,
            description: `${voucher.voucher_number} - ${voucher.party?.name || 'Unknown'}`,
            amount: parseFloat(voucher.total_amount),
            time: this.getRelativeTime(new Date(voucher.created_at)),
            user: {
              name: voucher.created_by?.name || 'System'
            }
          });
        });
      }

      return activities.slice(0, 10); // Return top 10 activities
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  },

  getRelativeTime(date: Date): string {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const mins = Math.floor(diffInSeconds / 60);
      return `${mins} min${mins > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  },

  async getComplianceDashboard(): Promise<ComplianceDashboard> {
    try {
      const response = await axiosInstance.get<ComplianceDashboard>('/return-generation/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance dashboard:', error);
      return {
        pending_count: 0,
        overdue_count: 0,
        due_this_week_count: 0,
        filed_this_month_count: 0,
      };
    }
  },

  async getTeamActivity(limit: number = 20): Promise<TeamActivity[]> {
    try {
      // Fetch recently completed tasks with user info
      const response = await axiosInstance.get<{ items: any[] }>('/tasks/', {
        params: {
          status: 'completed',
          limit,
          sort_by: 'completed_at',
          sort_dir: 'desc',
        }
      });

      const activities = response.data.items.map((task: any) => ({
        id: task.id,
        user_id: task.assigned_to_user_id || task.created_by_user_id,
        user_name: task.assigned_to?.full_name || task.assigned_to?.username || task.creator?.full_name || 'Unknown',
        action: 'completed' as const,
        task_id: task.id,
        task_title: task.title,
        timestamp: task.completed_at || task.updated_at || task.created_at,
      }));

      // Sort by timestamp descending (handles NULL completed_at mixing up backend sort)
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return activities;
    } catch (error) {
      console.error('Error fetching team activity:', error);
      return [];
    }
  },

  async getTaskCompletionStats(period: 'daily' | 'weekly' = 'daily'): Promise<TaskCompletionStats> {
    try {
      // Get all tasks and aggregate by user
      const [completedTasks, allTasks] = await Promise.all([
        axiosInstance.get<{ items: any[]; total: number }>('/tasks/', {
          params: {
            status: 'completed',
            limit: 500,
          }
        }),
        axiosInstance.get<{ items: any[]; total: number }>('/tasks/', {
          params: {
            limit: 500,
          }
        }),
      ]);

      // Calculate date threshold
      const now = new Date();
      const threshold = new Date();
      if (period === 'daily') {
        threshold.setHours(0, 0, 0, 0);
      } else {
        threshold.setDate(threshold.getDate() - 7);
      }

      // Filter completed tasks within period
      const recentCompleted = completedTasks.data.items.filter((task: any) => {
        const completedAt = new Date(task.completed_at || task.updated_at);
        return completedAt >= threshold;
      });

      // Group by user
      const userStats: Record<number, { user_name: string; completed: number; assigned: number }> = {};

      recentCompleted.forEach((task: any) => {
        const userId = task.assigned_to_user_id || task.created_by_user_id;
        if (!userId) return;

        if (!userStats[userId]) {
          userStats[userId] = {
            user_name: task.assigned_to?.full_name || task.assigned_to?.username || 'Unknown',
            completed: 0,
            assigned: 0,
          };
        }
        userStats[userId].completed++;
      });

      // Count assigned tasks per user
      allTasks.data.items.forEach((task: any) => {
        const userId = task.assigned_to_user_id;
        if (!userId) return;

        if (!userStats[userId]) {
          userStats[userId] = {
            user_name: task.assigned_to?.full_name || task.assigned_to?.username || 'Unknown',
            completed: 0,
            assigned: 0,
          };
        }
        userStats[userId].assigned++;
      });

      const stats = Object.entries(userStats).map(([userId, data]) => ({
        user_id: parseInt(userId),
        user_name: data.user_name,
        completed_count: data.completed,
        total_assigned: data.assigned || data.completed,
      }));

      // Sort by completion count
      stats.sort((a, b) => b.completed_count - a.completed_count);

      return {
        period,
        stats,
        team_total_completed: recentCompleted.length,
        team_target: period === 'daily' ? 20 : 100,
      };
    } catch (error) {
      console.error('Error fetching task completion stats:', error);
      return {
        period,
        stats: [],
        team_total_completed: 0,
        team_target: period === 'daily' ? 20 : 100,
      };
    }
  }
};